import { useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaImage, FaMapMarkerAlt, FaTimes, FaEye, FaTrash } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { MapSelector } from "../../components/map";
import MapDisplay from "../../components/map/MapDisplay";
import { createPlace } from "../../services/placeService";
import { updatePost, deletePost, getPostById } from "../../services/postService";
import { compressImage } from "../../utils/imageOptimizer";
import supabase from '../../services/supabase';
import "./EditPost.css";

export default function EditPost() {
  const { postId } = useParams();
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [country, setCountry] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [content, setContent] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [showMapSelector, setShowMapSelector] = useState(false);
  const [mapLocation, setMapLocation] = useState(null);
  const [originalPost, setOriginalPost] = useState(null);
  const [fetchingPost, setFetchingPost] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is authenticated
    const getUser = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate("/login");
          return;
        }
        setUser(session.user);
      } catch (error) {
        console.error("Authentication error:", error);
        alert("Authentication error. Please try logging in again.");
        navigate("/login");
      }
    };
    
    getUser();
  }, [navigate]);

  // Fetch existing post data
  useEffect(() => {
    const fetchPost = async () => {
      if (!postId) return;
      
      try {
        setFetchingPost(true);
        const post = await getPostById(postId);
        
        if (!post) {
          alert("Post not found");
          navigate("/profile");
          return;
        }

        // Check if user owns this post
        if (post.user_id !== user?.id) {
          alert("You can only edit your own posts");
          navigate("/profile");
          return;
        }

        setOriginalPost(post);
        setTitle(post.title || "");
        setDate(post.date || "");
        setCountry(post.country || "");
        setTags(post.tags || []);
        setContent(post.content || "");
        setWordCount(post.content ? post.content.split(' ').length : 0);
        
        // Handle existing images
        if (post.post_images && post.post_images.length > 0) {
          setImagePreviews(post.post_images.map(img => img.url));
        } else if (post.image_url) {
          setImagePreviews([post.image_url]);
        }

        // Handle location
        if (post.location) {
          setMapLocation(post.location);
        }

      } catch (error) {
        console.error("Error fetching post:", error);
        alert("Error loading post. Please try again.");
        navigate("/profile");
      } finally {
        setFetchingPost(false);
      }
    };

    if (user && postId) {
      fetchPost();
    }
  }, [postId, user, navigate]);

  // If a location is selected, update the country field automatically
  useEffect(() => {
    if (mapLocation && mapLocation.name) {
      // Extract country from location name (e.g., "Istanbul, Turkey" -> "Turkey")
      const locationParts = mapLocation.name.split(', ');
      if (locationParts.length > 1) {
        setCountry(locationParts[locationParts.length - 1]);
      }
    }
  }, [mapLocation]);
  
  // Generate image previews when images are selected
  useEffect(() => {
    const generatePreviews = async () => {
      const previews = [];
      
      for (const image of images) {
        const reader = new FileReader();
        const preview = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(image);
        });
        previews.push(preview);
      }
      
      setImagePreviews(previews);
    };
    
    if (images.length > 0) {
      generatePreviews();
    }
  }, [images]);

  const handleAddTag = () => {
    // Trim and sanitize tag input
    const sanitizedTag = tagInput.trim();
    if (sanitizedTag && !tags.includes(sanitizedTag)) {
      setTags([...tags, sanitizedTag]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    setWordCount(newContent.split(' ').filter(word => word.length > 0).length);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length + images.length > 5) {
      alert("You can upload a maximum of 5 images");
      return;
    }
    
    setImages([...images, ...imageFiles]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
    if (currentPreviewIndex >= imagePreviews.length - 1) {
      setCurrentPreviewIndex(Math.max(0, imagePreviews.length - 2));
    }
  };

  const nextImage = () => {
    setCurrentPreviewIndex((prev) => 
      prev === imagePreviews.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentPreviewIndex((prev) => 
      prev === 0 ? imagePreviews.length - 1 : prev - 1
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim()) {
      alert("Please enter a title");
      return;
    }

    if (!content.trim()) {
      alert("Please enter some content");
      return;
    }

    setLoading(true);

    try {
      // Prepare location data if we have it
      let locationData = null;
      if (mapLocation) {
        locationData = {
          name: mapLocation.name,
          lat: mapLocation.lat,
          lng: mapLocation.lng
        };
        console.log("Location data for post update:", locationData);
      }

      // Prepare post data
      const postData = {
        title: title.trim(),
        content: content.trim(),
        date: date,
        country: country,
        tags: tags
      };

      // Update the post
      await updatePost(postId, postData, images, locationData);

      // Navigate to the post detail page
      navigate(`/post/${postId}`);
      
    } catch (error) {
      console.error("Error updating post:", error);
      alert("Error updating post. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) {
      setShowDeleteConfirm(true);
      return;
    }

    setLoading(true);
    
    try {
      await deletePost(postId);
      alert("Post deleted successfully");
      navigate("/profile");
    } catch (error) {
      console.error("Error deleting post:", error);
      alert("Error deleting post. Please try again.");
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleViewPost = () => {
    navigate(`/post/${postId}`);
  };

  const handleCancel = () => {
    navigate(`/post/${postId}`);
  };

  const handleMapButton = () => {
    setShowMapSelector(true);
  };

  const handleSelectLocation = (location) => {
    setMapLocation(location);
    setShowMapSelector(false);
  };

  const handleCloseMap = () => {
    setShowMapSelector(false);
  };

  const handleRemoveLocation = () => {
    setMapLocation(null);
    setCountry("");
  };

  if (fetchingPost) {
    return (
      <div className="edit-post-container">
        <div className="loading-message">Loading post...</div>
      </div>
    );
  }

  return (
    <div className="edit-post-container wanderlog-style">
      <div className="edit-post-header">
        <button className="back-button" onClick={handleCancel}>
          <FaChevronLeft /> Cancel
        </button>
        <h1>Edit Post</h1>
        <div className="header-actions">
          <button 
            className="view-post-button" 
            onClick={handleViewPost}
            disabled={loading}
          >
            <FaEye /> View Post
          </button>
        </div>
      </div>
      
      <div className="two-column-layout">
        {/* Left column: Post content */}
        <div className="edit-post-form">
          <div className="form-section">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter post title..."
              maxLength={100}
              required
            />
          </div>

          <div className="form-section">
            <label htmlFor="content">Content *</label>
            <textarea
              id="content"
              value={content}
              onChange={handleContentChange}
              placeholder="Share your experience..."
              rows={6}
              maxLength={1000}
              required
            />
            <div className="word-count">{wordCount}/1000 words</div>
          </div>

          <div className="form-section">
            <label>Images</label>
            <div className="image-upload-container">
              {imagePreviews.length > 0 ? (
                <div className="image-preview-container">
                  <img
                    src={imagePreviews[currentPreviewIndex]}
                    alt={`Preview ${currentPreviewIndex + 1}`}
                    className="image-preview"
                  />
                  {imagePreviews.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="nav-button prev"
                        onClick={prevImage}
                      >
                        <FaChevronLeft />
                      </button>
                      <button
                        type="button"
                        className="nav-button next"
                        onClick={nextImage}
                      >
                        <FaChevronRight />
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="remove-image"
                    onClick={() => removeImage(currentPreviewIndex)}
                  >
                    <FaTimes />
                  </button>
                  <div className="image-counter">
                    {currentPreviewIndex + 1} / {imagePreviews.length}
                  </div>
                </div>
              ) : (
                <label htmlFor="image-upload" className="image-upload-button">
                  <FaImage />
                  Add Images
                </label>
              )}
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="image-input"
                id="image-upload"
              />
              <span className="image-limit">Max 5 images</span>
            </div>
          </div>

          <div className="form-section">
            <label>Tags</label>
            <div className="tag-input-container">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                placeholder="Add tags..."
              />
              <button type="button" onClick={handleAddTag}>
                Add
              </button>
            </div>
            <div className="tags-container">
              {tags.map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="remove-tag"
                  >
                    <FaTimes />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="form-section">
            <label htmlFor="date">Date</label>
            <input
              type="date"
              id="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div className="form-section">
            <label htmlFor="country">Country</label>
            <input
              type="text"
              id="country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="Enter country..."
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="delete-button"
              onClick={handleDelete}
              disabled={loading}
            >
              <FaTrash />
              {showDeleteConfirm ? "Confirm Delete" : "Delete Post"}
            </button>
            
            <div className="save-actions">
              <button
                type="button"
                className="cancel-button"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="save-button"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
        
        {/* Right column: Map */}
        <div className="map-column">
          <div className="form-section">
            <label>Location</label>
            <div className="location-selector">
              <button
                type="button"
                onClick={handleMapButton}
                className="map-selector-button"
              >
                <FaMapMarkerAlt />
                {mapLocation ? mapLocation.name : "Select location"}
              </button>
              {mapLocation && (
                <button
                  type="button"
                  onClick={handleRemoveLocation}
                  className="clear-location"
                >
                  <FaTimes />
                </button>
              )}
            </div>
            {mapLocation && (
              <div className="selected-location">
                <MapDisplay location={mapLocation} />
              </div>
            )}
          </div>
        </div>
      </div>

      {showMapSelector && (
        <div className="map-selector-modal">
          <div className="map-selector-content">
            <div className="map-selector-header">
              <h3>Select Location</h3>
              <button
                onClick={handleCloseMap}
                className="close-button"
              >
                <FaTimes />
              </button>
            </div>
            <MapSelector
              onLocationSelect={handleSelectLocation}
            />
          </div>
        </div>
      )}
    </div>
  );
}
